import { useState, useMemo, useEffect, useCallback, useRef } from "react";
import { Button, Checkbox, Icon, Modal, Spinner, Tag, Text } from "@ttc3k/trekker";
import { Box, Center, Flex } from "styled-system/jsx";
import { useTranslation } from "react-i18next";
import {
  SurveyQuestionList,
  formatBusinessScopedAnswer,
} from "shared/components/organisms/SurveyQuestionInput";
import { Skeleton } from "shared/components/atoms/Skeleton";
import {
  ActivityLogManyDocument,
  EnumOwnerType,
  EnumSurveyQuestionType,
  Multilingual,
  Owner,
  SurveyFragmentFragment,
  useSurveyResponseForBroadcastResourceLazyQuery,
  useSurveyResponseSaveForBroadcastMutation,
  useSurveyResponseSubmitForBroadcastMutation,
} from "gql/generated";
import _ from "lodash";
import { format } from "date-fns";
import { CheckCircleSolid, FloppyDisk } from "iconoir-react";
import { HtmlContent } from "shared/components/molecules/HtmlContent";
import { isLexicalHtmlEmpty } from "shared/components/molecules/TextEditor";
import { formatAddressSearchResponse } from "shared/components/organisms/SurveyQuestionInput/addressSearchResponse";
import {
  getBooleanResponseDisplay,
  getDefaultSurveyQuestionResponse,
  isBooleanResponseAnswered,
} from "shared/components/organisms/SurveyQuestionInput/surveyQuestionCheckbox";
import { SurveyDesignField } from "shared/components/organisms/SurveyQuestionInput/SurveyDesignField";
import { isSurveyDesignFieldType } from "shared/components/organisms/SurveyQuestionInput/surveyQuestionDesign";
import {
  clearHiddenResponses,
  isQuestionVisible,
  isToggleOn,
  isToggleQuestionType,
} from "shared/components/organisms/SurveyQuestionInput/surveyQuestionVisibility";
import { getMultilingualString } from "shared/utils";
import {
  getScopingToggle,
  hasPerBusinessQuestions,
  normalizeBusinessScopedResponses,
  setBusinessAnswer,
  type BusinessAnswer,
  type BusinessScoped,
} from "@shared/businessScope";
import type { UserOperator } from "widgets/booker/contexts/BookerContextProvider/BookerContext";

type HistoryEntry = {
  action?: string | null;
  timestamp?: string | null;
  savedBy?: string | null;
  personName?: string | null;
  reviewStatus?: string | null;
  reviewNote?: string | null;
};

type BroadcastFormInlineProps = {
  survey: SurveyFragmentFragment;
  onSuccess?: () => void;
  isPreview?: boolean;
  broadcastId?: string;
  resourceId?: string;
  personId?: string;
  personName?: string;
  broadcastName?: Multilingual;
  broadcastOwner?: Owner;
  allowSaveAndEdit?: boolean;
  isSharedForm?: boolean;
  allowResubmission?: boolean;
  submissionIndex?: number;
  isNewSubmission?: boolean;
  onDirtyChange?: (dirty: boolean) => void;
  /** After first save of a multi-submission "new" draft, called with the persisted index so further saves upsert the same row. */
  onDraftSaved?: (submissionIndex: number) => void;
  /** Businesses chosen for a new submission, primary first. */
  operatorIds?: string[];
  businesses?: BusinessScoped[];
  /** Offered as a business picker when the form asks per-business questions and none were chosen. */
  userOperators?: UserOperator[];
  readOnly?: boolean;
  savedByPersonId?: string;
};

type FormResponse = {
  questionData: {
    _id: string;
    name?: { en?: string | null; fr?: string | null; es?: string | null } | null;
    type?: string | null;
    parentQuestion?: string | null;
    businessScope?: string | null;
  };
  response: string[];
  byBusiness?: BusinessAnswer[];
};

const snapshotResponses = (responses: FormResponse[]) =>
  JSON.stringify(
    responses.map(r => ({
      id: r.questionData._id,
      response: r.response,
      byBusiness: r.byBusiness ?? null,
    })),
  );

type FormStatus = "idle" | "draft" | "submitted" | "pending_review" | "rejected";

const SAVED_FEEDBACK_MS = 3_000;

function isConflictError(err: unknown): boolean {
  if (err instanceof Error && err.message.includes("CONFLICT")) return true;
  const gqlErr = err as { graphQLErrors?: { message: string }[] };
  return gqlErr?.graphQLErrors?.some(e => e.message.includes("CONFLICT")) ?? false;
}

export const BroadcastFormInline = ({
  survey,
  onSuccess,
  isPreview = false,
  broadcastId,
  resourceId,
  personId,
  broadcastOwner,
  allowSaveAndEdit = false,
  isSharedForm = false,
  allowResubmission = false,
  submissionIndex,
  isNewSubmission = false,
  onDirtyChange,
  onDraftSaved,
  operatorIds,
  businesses,
  userOperators,
  readOnly = false,
  savedByPersonId,
}: BroadcastFormInlineProps) => {
  const { t } = useTranslation("home", {
    keyPrefix: "VIEWS.BROADCAST",
  });

  const [saveForBroadcast, { loading: saving }] =
    useSurveyResponseSaveForBroadcastMutation();
  const [submitForBroadcast, { loading: submitting }] =
    useSurveyResponseSubmitForBroadcastMutation();
  const [loadExisting] =
    useSurveyResponseForBroadcastResourceLazyQuery();

  const shouldLoadExisting = !isPreview && !isNewSubmission && !!broadcastId && !!resourceId && !!personId;
  const loadKey = `${broadcastId}|${resourceId}|${personId}|${submissionIndex}`;
  const [isLoading, setIsLoading] = useState(shouldLoadExisting);

  const [formStatus, setFormStatus] = useState<FormStatus>("idle");
  const [existingVersion, setExistingVersion] = useState<number | undefined>();
  const [history, setHistory] = useState<HistoryEntry[]>([]);
  const [isEditing, setIsEditing] = useState(false);
  const [showSavedFeedback, setShowSavedFeedback] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [conflictDetected, setConflictDetected] = useState(false);
  const [reloadTick, setReloadTick] = useState(0);
  const [isFormStale, setIsFormStale] = useState(false);
  const [storedResponses, setStoredResponses] = useState<FormResponse[]>([]);
  const [loadedBusinesses, setLoadedBusinesses] = useState<BusinessScoped[]>([]);
  const [pickedOperatorIds, setPickedOperatorIds] = useState<string[]>([]);

  const savedResponsesRef = useRef<string>("");

  const activeQuestions = useMemo(
    () => survey.questions?.filter(q => q?.isActive) ?? [],
    [survey.questions],
  );

  const perBusinessEnabled = !!survey.perBusinessAnswersEnabled;
  const asksPerBusiness = useMemo(
    () => perBusinessEnabled && hasPerBusinessQuestions(activeQuestions),
    [perBusinessEnabled, activeQuestions],
  );
  const showBusinessPicker =
    asksPerBusiness &&
    !businesses?.length &&
    loadedBusinesses.length === 0 &&
    (userOperators?.length ?? 0) > 0;
  useEffect(() => {
    if (userOperators?.length === 1) {
      setPickedOperatorIds([userOperators[0].entityId]);
    }
  }, [userOperators]);
  const formBusinesses = useMemo<BusinessScoped[]>(() => {
    if (!perBusinessEnabled) return [];
    if (businesses?.length) return businesses;
    if (loadedBusinesses.length) return loadedBusinesses;
    return pickedOperatorIds.map(id => ({
      operatorId: id,
      businessName: userOperators?.find(op => op.entityId === id)?.name ?? null,
    }));
  }, [perBusinessEnabled, businesses, loadedBusinesses, pickedOperatorIds, userOperators]);
  const formOperatorIds = useMemo(
    () => formBusinesses.map(b => b.operatorId),
    [formBusinesses],
  );
  /** Businesses to record on save: chosen here, not already on the submission. */
  const operatorIdsToSave =
    operatorIds?.length
      ? operatorIds
      : showBusinessPicker && pickedOperatorIds.length
        ? pickedOperatorIds
        : undefined;
  const normalizeResponses = useCallback(
    (next: FormResponse[]) =>
      clearHiddenResponses(
        normalizeBusinessScopedResponses(next, formOperatorIds) as FormResponse[],
      ),
    [formOperatorIds],
  );

  const defaultResponses = useMemo(
    () =>
      activeQuestions.map(q => ({
        questionData: q,
        response: getDefaultSurveyQuestionResponse(q),
      })),
    [activeQuestions],
  );

  const [responses, setResponses] = useState<FormResponse[]>(defaultResponses);

  useEffect(() => {
    setResponses(defaultResponses);
  }, [defaultResponses]);

  useEffect(() => {
    const currentKey = loadKey;

    setFormStatus("idle");
    setExistingVersion(undefined);
    setHistory([]);
    setIsEditing(false);
    setShowSavedFeedback(false);
    setIsDirty(false);
    setIsFormStale(false);
    setStoredResponses([]);
    setLoadedBusinesses([]);
    savedResponsesRef.current = "";

    if (!broadcastId || !resourceId || !personId || isPreview || isNewSubmission) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    loadExisting({
      variables: {
        broadcastId,
        resourceId,
        personId,
        isShared: isSharedForm || undefined,
        submissionIndex,
      },
    }).then((result) => {
      if (currentKey !== `${broadcastId}|${resourceId}|${personId}|${submissionIndex}`) return;

      const existing = result.data?.surveyResponseForBroadcastResource;
      if (!existing) {
        setIsLoading(false);
        return;
      }
      setExistingVersion(existing.version ?? undefined);
      setHistory((existing.history as HistoryEntry[]) ?? []);
      setLoadedBusinesses(
        (existing.ownerBusinesses ?? []).map(b => ({
          operatorId: b.operatorId,
          businessName: b.businessName ?? null,
        })),
      );

      if (existing.status === "SUBMITTED") {
        setFormStatus("submitted");
      } else if (existing.status === "PENDING_REVIEW") {
        setFormStatus("pending_review");
      } else if (existing.status === "REJECTED") {
        setFormStatus("rejected");
      } else if (existing.status === "DRAFT") {
        setFormStatus("draft");
      }

      // Detect stale form (survey edited after this response was saved).
      // Collaborative forms are exempt — they may change often and that's OK.
      const stale =
        !isSharedForm &&
        !!existing.surveyUpdatedAt &&
        !!survey.updatedDateISO &&
        new Date(survey.updatedDateISO) > new Date(existing.surveyUpdatedAt);
      setIsFormStale(stale);

      if (existing.responses?.length) {
        // Store the raw responses with their embedded questionData for stale rendering
        setStoredResponses(
          existing.responses
            .filter((r): r is NonNullable<typeof r> => !!r)
            .map(r => ({
              questionData: {
                _id: r.questionData._id,
                name: r.questionData.name,
                type: r.questionData.type,
                parentQuestion: r.questionData.parentQuestion,
                businessScope: r.questionData.businessScope,
              },
              response: _.compact(r.response),
              ...(r.byBusiness?.length
                ? {
                    byBusiness: _.compact(r.byBusiness).map(b => ({
                      operatorId: b.operatorId,
                      response: _.compact(b.response),
                    })),
                  }
                : {}),
            })),
        );

        setResponses(prev => {
          const updated = prev.map(r => {
            const savedResp = existing.responses?.find(
              (s) =>
                s?.questionData?._id?.toString() === r.questionData._id,
            );
            if (!savedResp) return r;
            return {
              ...r,
              response: _.compact(savedResp.response ?? r.response),
              ...(savedResp.byBusiness?.length
                ? {
                    byBusiness: _.compact(savedResp.byBusiness).map(b => ({
                      operatorId: b.operatorId,
                      response: _.compact(b.response),
                    })),
                  }
                : {}),
            };
          });
          savedResponsesRef.current = snapshotResponses(updated);
          return updated;
        });
      }
      setIsLoading(false);
    }).catch(() => {
      if (currentKey !== `${broadcastId}|${resourceId}|${personId}|${submissionIndex}`) return;
      setIsLoading(false);
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadKey, isPreview, isNewSubmission, isSharedForm, loadExisting, reloadTick]);

  // Track dirty state (debounced to avoid JSON.stringify on every keystroke)
  useEffect(() => {
    if (!savedResponsesRef.current) return;
    const timer = setTimeout(() => {
      const currentSnapshot = snapshotResponses(responses);
      const dirty = currentSnapshot !== savedResponsesRef.current;
      setIsDirty(dirty);
      onDirtyChange?.(dirty);
    }, 300);
    return () => clearTimeout(timer);
  }, [responses, onDirtyChange]);

  // Notify parent on unmount that we're clean
  useEffect(() => {
    return () => {
      onDirtyChange?.(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // beforeunload handler
  useEffect(() => {
    if (!isDirty) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [isDirty]);

  const handleResponseChange = useCallback(
    (questionId: string, response: string[]) => {
      setResponses(prev =>
        normalizeResponses(
          prev.map(r =>
            r.questionData._id === questionId ? { ...r, response } : r,
          ),
        ),
      );
    },
    [normalizeResponses],
  );

  const handleBusinessResponseChange = useCallback(
    (questionId: string, operatorId: string, response: string[]) => {
      setResponses(prev =>
        normalizeResponses(
          prev.map(r =>
            r.questionData._id === questionId
              ? { ...r, byBusiness: setBusinessAnswer(r.byBusiness, operatorId, response) }
              : r,
          ),
        ),
      );
    },
    [normalizeResponses],
  );

  useEffect(() => {
    setResponses(prev => normalizeResponses(prev));
  }, [normalizeResponses]);

  const responsesByQuestionId = useMemo(
    () => new Map(responses.map(r => [r.questionData._id, r])),
    [responses],
  );

  const isRequiredFilled = useMemo(() => {
    if (showBusinessPicker && pickedOperatorIds.length === 0) return false;
    return responses.every(r => {
      const q = activeQuestions.find(
        qq => qq?._id === r.questionData._id,
      );
      if (!q || isSurveyDesignFieldType(q.type)) return true;
      if (!q.required) return true;
      if (!isQuestionVisible(r.questionData, responses)) return true;
      const isAnswered = (response: string[]) => {
        if (q.type === EnumSurveyQuestionType.Boolean)
          return isBooleanResponseAnswered(q, response);
        if (q.type === EnumSurveyQuestionType.RichText)
          return !isLexicalHtmlEmpty(response?.[0]);
        return (
          Array.isArray(response) &&
          response.length > 0 &&
          _.compact(response).length > 0
        );
      };
      if (formBusinesses.length > 0 && getScopingToggle(r.questionData, responses)) {
        return (r.byBusiness ?? []).every(b => isAnswered(b.response));
      }
      return isAnswered(r.response);
    });
  }, [responses, activeQuestions, formBusinesses, showBusinessPicker, pickedOperatorIds]);

  const buildResponsePayload = useCallback(() => {
    return responses
      .filter(r => {
        const q = activeQuestions.find(
          qq => qq?._id === r.questionData._id,
        );
        return q && !isSurveyDesignFieldType(q.type);
      })
      .map(r => {
        const q = activeQuestions.find(
          qq => qq?._id === r.questionData._id,
        )!;
        return {
          questionData: {
            _id: q._id,
            isActive: q.isActive,
            name: q.name,
            options: q.options,
            owner: q.owner,
            required: q.required,
            type: q.type,
            parentQuestion: q.parentQuestion,
            businessScope: q.businessScope,
          },
          response: _.compact(r.response),
          ...(r.byBusiness?.length
            ? {
                byBusiness: r.byBusiness.map(b => ({
                  operatorId: b.operatorId,
                  response: _.compact(b.response),
                })),
              }
            : {}),
        };
      });
  }, [responses, activeQuestions]);

  const ownerArgs = useMemo(() => {
    const base = !broadcastOwner
      ? {}
      : broadcastOwner.type === EnumOwnerType.Organization
        ? { organizationId: broadcastOwner.entityId }
        : { operatorId: broadcastOwner.entityId };
    if (operatorIdsToSave?.length) {
      return {
        ...base,
        operatorId: operatorIdsToSave[0],
        operatorIds: operatorIdsToSave,
      };
    }
    return base;
  }, [broadcastOwner, operatorIdsToSave]);

  const markClean = useCallback(() => {
    savedResponsesRef.current = snapshotResponses(responses);
    setIsDirty(false);
    onDirtyChange?.(false);
  }, [responses, onDirtyChange]);

  const handleReload = useCallback(() => {
    setConflictDetected(false);
    setReloadTick(t => t + 1);
  }, []);

  const handleSaveDraft = useCallback(async () => {
    if (isPreview || readOnly || !broadcastId || !resourceId || !personId) return;
    try {
      const result = await saveForBroadcast({
        variables: {
          surveyId: survey._id,
          broadcastId,
          resourceId,
          personId,
          savedByPersonId: savedByPersonId || undefined,
          responses: buildResponsePayload(),
          expectedVersion: existingVersion,
          submissionIndex,
          isShared: isSharedForm || undefined,
          isNewSubmission: isNewSubmission || undefined,
          ...ownerArgs,
        },
      });
      const sr = result.data?.surveyResponseSaveForBroadcast;
      if (sr) {
        setExistingVersion(sr.version ?? undefined);
        setFormStatus("draft");
        setHistory((sr.history as HistoryEntry[]) ?? []);
        markClean();

        if (isNewSubmission && sr.submissionIndex != null) {
          onDraftSaved?.(Number(sr.submissionIndex));
        }

        setShowSavedFeedback(true);
        setTimeout(() => setShowSavedFeedback(false), SAVED_FEEDBACK_MS);
      }
    } catch (err) {
      if (isConflictError(err)) {
        setConflictDetected(true);
      }
    }
  }, [
    isPreview, readOnly, broadcastId, resourceId, personId, savedByPersonId,
    saveForBroadcast, survey._id, buildResponsePayload,
    existingVersion, ownerArgs, markClean, submissionIndex, isSharedForm, isNewSubmission,
    onDraftSaved,
  ]);

  const handleSubmit = async () => {
    if (isPreview || readOnly || !broadcastId || !resourceId || !personId) return;
    try {
      const result = await submitForBroadcast({
        variables: {
          surveyId: survey._id,
          broadcastId,
          resourceId,
          personId,
          savedByPersonId: savedByPersonId || undefined,
          responses: buildResponsePayload(),
          expectedVersion: existingVersion,
          submissionIndex,
          isShared: isSharedForm || undefined,
          isNewSubmission: isNewSubmission || undefined,
          ...ownerArgs,
        },
        refetchQueries: [{ query: ActivityLogManyDocument }],
      });
      const sr = result.data?.surveyResponseSubmitForBroadcast;
      if (sr) {
        setExistingVersion(sr.version ?? undefined);
        setHistory((sr.history as HistoryEntry[]) ?? []);
        markClean();
        if (sr.status === "PENDING_REVIEW") {
          setFormStatus("pending_review");
        } else {
          setFormStatus("submitted");
        }
        setIsEditing(false);
      }
      onSuccess?.();
    } catch (err) {
      if (isConflictError(err)) {
        setConflictDetected(true);
      }
    }
  };

  const isLocked = isFormStale || (formStatus === "submitted" && !isEditing);
  const isPendingReview = formStatus === "pending_review";
  const isRejected = formStatus === "rejected";
  const showForm =
    !isLoading &&
    !isFormStale &&
    (formStatus === "idle" ||
    formStatus === "draft" ||
    formStatus === "rejected" ||
    formStatus === "pending_review" ||
    isEditing);

  const loading = saving || submitting;

  if (isLoading) {
    return (
      <Center py="600">
        <Spinner w="38px" h="38px" />
      </Center>
    );
  }

  return (
    <Flex flexDir="column" gap="400">
      <Modal
        isOpen={conflictDetected}
        onClose={() => setConflictDetected(false)}
        title={t("FORM_CONFLICT_TITLE")}
        primaryAction={{
          label: t("FORM_CONFLICT_REFRESH"),
          onClick: handleReload,
        }}
        secondaryAction={{
          label: t("FORM_CONFLICT_DISMISS"),
          onClick: () => setConflictDetected(false),
        }}
      >
        <Text>{t("FORM_CONFLICT_MESSAGE")}</Text>
      </Modal>

      {/* Rejection banner — shown when rejected OR when resubmitted after rejection */}
      {(isRejected || isPendingReview) && (() => {
        const rejectionEntry = [...(history ?? [])].reverse().find(
          (e) => e.reviewStatus === "REJECTED",
        );
        if (!rejectionEntry) return null;
        return (
          <Flex
            flexDir="column"
            gap="100"
            p="300"
            borderRadius="150"
            bgColor="bg.error.lighter"
            borderWidth="1px"
            borderColor="border.error.light"
            borderStyle="solid"
          >
            <Text visual="bodySemiBold" color="text.error.mid">
              {t("FORM_REJECTED")}
            </Text>
            {rejectionEntry.reviewNote && (
              <Text visual="bodyRegular" color="text.error.mid">
                {rejectionEntry.reviewNote}
              </Text>
            )}
          </Flex>
        );
      })()}

      {/* Pending review banner — form is still editable */}
      {isPendingReview && (
        <Flex
          flexDir="column"
          gap="100"
          p="300"
          borderRadius="150"
          bgColor="bg.warning.lighter"
          borderWidth="1px"
          borderColor="border.warning.light"
          borderStyle="solid"
        >
          <Text visual="bodySemiBold" color="text.warning.mid">
            {t("FORM_STATUS_PENDING_REVIEW")}
          </Text>
          <Text visual="bodyRegular" color="text.warning.mid">
            {t("FORM_PENDING_REVIEW_EDITABLE")}
          </Text>
        </Flex>
      )}

      {/* Read-only responses for locked state */}
      {isLocked && (
        <Skeleton alignItems="stretch" minH="unset">
          <ReadOnlyResponses
            responses={isFormStale ? storedResponses : responses}
            activeQuestions={activeQuestions}
            useStoredQuestions={isFormStale}
            skippedLabel={t("FORM_SKIPPED")}
            businesses={formBusinesses}
          />
          {isFormStale ? (
            <Text visual="smallRegular" color="text.light">
              {t("FORM_NO_LONGER_EDITABLE")}
            </Text>
          ) : (isSharedForm || allowResubmission) && !isPreview && !readOnly ? (
            <Flex justifyContent="flex-end">
              <Button
                mode="light"
                onClick={() => setIsEditing(true)}
                type="button"
                size="sm"
              >
                {t("FORM_EDIT")}
              </Button>
            </Flex>
          ) : null}
        </Skeleton>
      )}

      {/* Editable form */}
      {showForm && (
        <>
          <Box borderTop="1px solid" borderColor="border.light" />
          {showBusinessPicker && userOperators && (
            <Flex flexDir="column" gap="100">
              <Text visual="smallSemiBold" color="text.dark">
                {t("FORM_SELECT_BUSINESSES")}
              </Text>
              <Text visual="smallRegular" color="text.mid">
                {t("FORM_SELECT_BUSINESSES_SUBTITLE")}
              </Text>
              {userOperators.map(op => (
                <Checkbox
                  key={op.entityId}
                  label={op.name}
                  checked={pickedOperatorIds.includes(op.entityId)}
                  onCheckedChange={e =>
                    setPickedOperatorIds(prev =>
                      e.checked === true
                        ? [...prev.filter(id => id !== op.entityId), op.entityId]
                        : prev.filter(id => id !== op.entityId),
                    )
                  }
                />
              ))}
            </Flex>
          )}
          {!showBusinessPicker && formBusinesses.length > 1 && (
            <Text visual="smallRegular" color="text.mid">
              {t("FORM_BUSINESSES_LABEL", {
                names: formBusinesses.map(b => b.businessName ?? b.operatorId).join(", "),
              })}
            </Text>
          )}
          <SurveyQuestionList
            questions={activeQuestions}
            getResponse={q => responsesByQuestionId.get(q._id)?.response ?? []}
            onResponseChange={handleResponseChange}
            businesses={formBusinesses}
            getBusinessAnswers={q => responsesByQuestionId.get(q._id)?.byBusiness ?? []}
            onBusinessResponseChange={handleBusinessResponseChange}
            organizationId={
              broadcastOwner?.type === EnumOwnerType.Organization
                ? broadcastOwner.entityId
                : undefined
            }
            gap="300"
          />
          {!isPreview && !readOnly && (
            <Flex justifyContent="flex-end" gap="150" alignItems="center">
              {allowSaveAndEdit && showSavedFeedback && (
                <Text visual="smallMedium" color="text.success.mid">
                  {t("FORM_SAVED")}
                </Text>
              )}
              {allowSaveAndEdit && (
                <Button
                  mode="light"
                  onClick={handleSaveDraft}
                  disabled={loading}
                  isLoading={saving}
                  type="button"
                  Icon={FloppyDisk}
                  aria-label={t("FORM_SAVE_DRAFT")}
                />
              )}
              <Button
                visual="accent.blue"
                onClick={handleSubmit}
                disabled={!isRequiredFilled || loading}
                isLoading={submitting}
                type="button"
              >
                {isPendingReview
                  ? t("FORM_UPDATE_SUBMISSION")
                  : isSharedForm ? t("FORM_COMPLETE") : t("FORM_SUBMIT")}
              </Button>
            </Flex>
          )}
          <Box borderBottom="1px solid" borderColor="border.light" />
        </>
      )}

      {/* History log */}
      {history.length > 0 && <HistoryLog history={history} t={t} />}
    </Flex>
  );
};

// --------------- Sub-components ---------------

function ReadOnlyResponses({
  responses,
  activeQuestions,
  useStoredQuestions = false,
  skippedLabel,
  businesses = [],
}: {
  businesses?: BusinessScoped[];
  responses: FormResponse[];
  activeQuestions: Array<{
    _id: string;
    name?: { en?: string | null; fr?: string | null; es?: string | null } | null;
    type?: string | null;
    parentQuestion?: string | null;
    businessScope?: string | null;
  } | null>;
  useStoredQuestions?: boolean;
  skippedLabel: string;
}) {
  if (useStoredQuestions) {
    return (
      <Flex flexDir="column" gap="200">
        {responses.map(r => {
          if (isSurveyDesignFieldType(r.questionData.type)) {
            return (
              <Box key={r.questionData._id}>
                <SurveyDesignField question={r.questionData} />
              </Box>
            );
          }
          const isRich = r.questionData.type === EnumSurveyQuestionType.RichText;
          const richHtml = r.response?.[0] ?? "";
          const formatOne = (answer: string[]) =>
            isToggleQuestionType(r.questionData.type)
              ? isToggleOn(answer) ? "Yes" : "No"
            : r.questionData.type === EnumSurveyQuestionType.Boolean
              ? getBooleanResponseDisplay(r.questionData, answer)
              : r.questionData.type === EnumSurveyQuestionType.WhereaboutsSearch
                ? answer?.[1] ?? answer?.[0] ?? "—"
                : r.questionData.type === EnumSurveyQuestionType.AddressSearch
                  ? formatAddressSearchResponse(answer)
                : _.compact(answer).join(", ") || "—";
          const perBusiness = formatBusinessScopedAnswer(
            r.questionData,
            r,
            responses,
            businesses,
            formatOne,
          );
          const displayValue = !isQuestionVisible(r.questionData, responses)
            ? skippedLabel
            : perBusiness ?? formatOne(r.response);
          return (
            <Box key={r.questionData._id}>
              <Text visual="smallMedium" color="text.mid" mb="50">
                {getMultilingualString(r.questionData.name ?? {})}
              </Text>
              {isRich ? (
                !isLexicalHtmlEmpty(richHtml) ? (
                  <HtmlContent html={richHtml} />
                ) : (
                  <Text visual="bodyRegular" color="text.dark">
                    —
                  </Text>
                )
              ) : (
                <Text visual="bodyRegular" color="text.dark">
                  {displayValue}
                </Text>
              )}
            </Box>
          );
        })}
      </Flex>
    );
  }

  return (
    <Flex flexDir="column" gap="200">
      {activeQuestions.map(q => {
        if (!q) return null;
        if (isSurveyDesignFieldType(q.type)) {
          return (
            <Box key={q._id}>
              <SurveyDesignField question={q} />
            </Box>
          );
        }
        const resp = responses.find(r => r.questionData._id === q._id);
        const isRich = q.type === EnumSurveyQuestionType.RichText;
        const richHtml = resp?.response?.[0] ?? "";
        const formatOne = (answer: string[]) =>
          isToggleQuestionType(q.type)
            ? isToggleOn(answer) ? "Yes" : "No"
          : q.type === EnumSurveyQuestionType.Boolean
            ? getBooleanResponseDisplay(q, answer)
            : q.type === EnumSurveyQuestionType.WhereaboutsSearch
              ? answer?.[1] ?? answer?.[0] ?? "—"
              : q.type === EnumSurveyQuestionType.AddressSearch
                ? formatAddressSearchResponse(answer ?? [])
              : _.compact(answer).join(", ") || "—";
        const perBusiness = formatBusinessScopedAnswer(
          q,
          resp,
          responses,
          businesses,
          formatOne,
        );
        const displayValue = !isQuestionVisible(q, responses)
          ? skippedLabel
          : perBusiness ?? formatOne(resp?.response ?? []);
        return (
          <Box key={q._id}>
            <Text visual="smallMedium" color="text.mid" mb="50">
              {getMultilingualString(q.name ?? {})}
            </Text>
            {isRich ? (
              !isLexicalHtmlEmpty(richHtml) ? (
                <HtmlContent html={richHtml} />
              ) : (
                <Text visual="bodyRegular" color="text.dark">
                  —
                </Text>
              )
            ) : (
              <Text visual="bodyRegular" color="text.dark">
                {displayValue}
              </Text>
            )}
          </Box>
        );
      })}
    </Flex>
  );
}

function HistoryLog({
  history,
  t,
}: {
  history: HistoryEntry[];
  t: (key: string, opts?: Record<string, unknown>) => string;
}) {
  const reversed = useMemo(() => [...history].reverse(), [history]);

  return (
    <Flex flexDir="column" gap="200">
      <Text visual="smallSemiBold" color="text.dark">
        {t("FORM_HISTORY")}
      </Text>
      {reversed.map((entry, idx) => {
        const isSave = entry.action === "SAVE";
        return (
          <Flex
            key={idx}
            flexDir="column"
            gap="50"
            pb="150"
            borderBottom={idx < reversed.length - 1 ? "1px solid" : undefined}
            borderColor="border.light"
          >
            <Flex alignItems="center" gap="100" justifyContent="space-between">
              <Flex alignItems="center" gap="100">
                <Icon
                  Element={isSave ? FloppyDisk : CheckCircleSolid}
                  color={isSave ? "icon.mid" : "icon.success.mid"}
                  size="xs"
                />
                <Text visual="smallMedium" color="text.dark">
                  {isSave ? t("FORM_ACTION_SAVED") : t("FORM_ACTION_SUBMITTED")}
                </Text>
                {entry.personName && (
                  <Text visual="smallRegular" color="text.light">
                    by {entry.personName}
                  </Text>
                )}
              </Flex>
              <Text visual="smallRegular" color="text.light">
                {entry.timestamp
                  ? format(new Date(entry.timestamp), "MMM d, yyyy h:mm a")
                  : ""}
              </Text>
            </Flex>
            {entry.reviewStatus && (
              <Flex alignItems="center" gap="100" ml="400">
                <Tag
                  visual={
                    entry.reviewStatus === "APPROVED"
                      ? "success"
                      : entry.reviewStatus === "REJECTED"
                        ? "destructive"
                        : "highlight"
                  }
                  mode="light"
                >
                  {entry.reviewStatus === "APPROVED"
                    ? "Approved"
                    : entry.reviewStatus === "REJECTED"
                      ? t("FORM_SUBMISSION_REJECTED")
                      : "Pending"}
                </Tag>
              </Flex>
            )}
            {entry.reviewNote && (
              <Text visual="smallRegular" color="text.light" ml="400">
                {entry.reviewNote}
              </Text>
            )}
          </Flex>
        );
      })}
    </Flex>
  );
}
